import React, { Suspense, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuickActionsSection } from '../components/organizer/QuickActionsSection';
import { MobileVisualTableMap } from '../components/mobile-toolkit/MobileVisualTableMap';

interface NavAction {
  icon: string;
  label: string;
  description: string;
  path: string;
}

const navActions: NavAction[] = [
  { icon: '🎭', label: 'Legacy Ball', description: 'Ticket tiers, pricing, capacity, and revenue', path: '/legacy-ball' },
  { icon: '💰', label: 'Budget', description: 'View and manage event budget', path: '/budget' },
  { icon: '📋', label: 'Master Plan', description: 'Tasks and milestones', path: '/tasks' },
  { icon: '📢', label: 'Announcements', description: 'Create and publish announcements', path: '/announcements' },
  { icon: '👥', label: 'Sub-Committees', description: 'Manage committee members and roles', path: '/committees' },
  { icon: '🔐', label: 'User Management', description: 'Manage users and roles', path: '/users' },
  { icon: '📍', label: 'Venues', description: 'Manage event venues', path: '/venues' },
  { icon: '🎯', label: 'Fundraising', description: 'Campaigns and donations', path: '/fundraising' },
  { icon: '🗺️', label: 'Guest Journey', description: 'Guest experience timeline', path: '/guest-journey' },
  { icon: '🎯', label: 'Processes & Flows', description: 'Workflow management', path: '/guest-experience' },
];

const dashboardLinks = [
  { area: 'registration', icon: '📝', label: 'Registration', color: '#2e7d32' },
  { area: 'check-in', icon: '✅', label: 'Check-In', color: '#00695c' },
  { area: 'events', icon: '📅', label: 'Events', color: '#1565c0' },
  { area: 'guest-services', icon: '🛎️', label: 'Guest Services', color: '#e65100' },
];

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 0.5rem 0' },
  subtitle: { color: '#666', fontSize: '0.95rem', marginBottom: '1.5rem' },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    cursor: 'pointer',
    borderBottom: '2px solid #e5e7eb',
    marginBottom: '0.75rem',
    marginTop: '1.5rem',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#1a6b3c',
    margin: 0,
  },
  chevron: {
    fontSize: '1rem',
    color: '#666',
    transition: 'transform 0.2s',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '1rem',
  },
  card: {
    padding: '1.25rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  cardIcon: { fontSize: '2rem', lineHeight: 1, flexShrink: 0 },
  cardContent: { flex: 1 },
  cardLabel: { fontSize: '1rem', fontWeight: 700, color: '#0f2d1f', marginBottom: '0.25rem' },
  cardDesc: { fontSize: '0.85rem', color: '#666', lineHeight: 1.4 },
  dashGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '0.75rem',
  },
};

function CollapsiblePanel({ title, icon, defaultOpen = false, children }: {
  title: string;
  icon: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        style={styles.sectionHeader}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span style={styles.sectionTitle}>{icon} {title}</span>
        <span style={{ ...styles.chevron, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
      </button>
      {open && children}
    </div>
  );
}


export function OrganizerPanelPage() {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Organizer Panel</h1>
      <p style={styles.subtitle}>Quick access to all organizer functions</p>

      {/* Quick Actions — always expanded */}
      <Suspense fallback={<div style={{ padding: '1rem', color: '#666' }}>Loading quick actions…</div>}>
        <QuickActionsSection />
      </Suspense>

      {/* Public Dashboards — collapsible */}
      <CollapsiblePanel title="Public Dashboards" icon="📺">
        <p style={{ fontSize: '0.8rem', color: '#666', margin: '0 0 0.75rem 0' }}>
          Open these links on display screens in venue areas. No login required.
        </p>
        <div style={styles.dashGrid}>
          {dashboardLinks.map((d) => (
            <a
              key={d.area}
              href={`/dashboard/${d.area}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '0.85rem 1rem',
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                textDecoration: 'none',
                transition: 'all 0.2s',
              }}
            >
              <span style={{ fontSize: '1.5rem', lineHeight: 1, flexShrink: 0 }}>{d.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f2d1f' }}>{d.label}</div>
                <div style={{ fontSize: '0.75rem', color: '#666' }}>Opens in new tab</div>
              </div>
              <span style={{ fontSize: '0.75rem', color: '#999' }}>↗</span>
            </a>
          ))}
        </div>
      </CollapsiblePanel>

      {/* Visual Table Map — collapsible */}
      <CollapsiblePanel title="Visual Table Map" icon="🪑">
        <MobileVisualTableMap />
      </CollapsiblePanel>

      {/* Navigate To — collapsible */}
      <CollapsiblePanel title="Navigate To" icon="🧭">
        <div style={styles.grid}>
          {navActions.map((action) => (
            <div
              key={action.path}
              style={styles.card}
              onClick={() => navigate(action.path)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(action.path); }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = '#1a6b3c';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(26,107,60,0.12)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = '#e0e0e0';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
              }}
            >
              <div style={styles.cardIcon}>{action.icon}</div>
              <div style={styles.cardContent}>
                <div style={styles.cardLabel}>{action.label}</div>
                <div style={styles.cardDesc}>{action.description}</div>
              </div>
            </div>
          ))}
        </div>
      </CollapsiblePanel>
    </div>
  );
}
